import type { TargetEntry, TargetEnvironment } from 'types/apis/configToolsSuite';

/**
 * Parses a target entry from the Status API. Supports two shapes:
 * 1. Nested: { targetEnv: { id, environmentName, name, type }, targetValue, status }
 * 2. Flat: { envId, env_id, environmentName, targetValue, status }
 */
export function parseTargetEntry(entry: any): TargetEntry | null {
  if (!entry) return null;

  const tg = entry.targetEnv;
  const rawEnvId =
    tg?.id ?? tg?.envId ?? entry?.envId ?? entry?.env_id ?? entry?.id;
  const envName = String(
    tg?.environmentName ?? tg?.environment_name ?? tg?.name ??
    entry?.environmentName ?? entry?.environment_name ?? entry?.name ?? ''
  ).trim();
  const hasEnvId = typeof rawEnvId !== 'undefined';
  const hasEnvName = envName !== '';

  if (!hasEnvId && !hasEnvName) return null;

  const envIdNum = typeof rawEnvId === 'number' ? rawEnvId : Number(rawEnvId);
  const resolvedEnvId = Number.isNaN(envIdNum) && hasEnvName ? 0 : envIdNum;
  if (Number.isNaN(envIdNum) && !hasEnvName) return null;

  const rawStatus = String(entry?.status ?? '').toLowerCase();
  const status =
    rawStatus === 'new' || rawStatus.includes('new')
      ? 'new'
      : rawStatus === 'modified' || rawStatus.includes('modified')
        ? 'modified'
        : rawStatus === 'conflict' || rawStatus.includes('conflict')
          ? 'conflict'
          : rawStatus === 'target_only' || rawStatus.includes('target_only')
            ? 'target_only'
            : rawStatus === 'success' || rawStatus.includes('success')
              ? 'unchanged'
              : 'unchanged';

  const oldValue =
    entry.oldValue ?? entry.old_value ?? entry.fromValue ?? entry.from_value ?? undefined;

  return {
    envId: resolvedEnvId,
    environmentName: envName || String(entry?.environmentName ?? entry?.environment_name ?? entry?.name ?? ''),
    type: String(tg?.type ?? entry?.type ?? ''),
    ...(oldValue !== undefined ? { oldValue } : {}),
    targetValue:
      entry.targetValue ?? entry.target_value ?? entry.toValue ?? entry.to_value ?? null,
    status,
  };
}

export function parseTargetEnvironments(container: any): TargetEnvironment[] {
  const raw = container?.targetEnvironments;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw
    .filter((e: any) => e && (typeof e.id === 'number' || typeof e.id === 'string'))
    .map((e: any) => ({
      id: Number(e.id),
      environmentName: String(e.environmentName ?? e.environment_name ?? e.name ?? ''),
      type: String(e.type ?? ''),
      stackName:
        e.stack_name != null ? String(e.stack_name) : e.stackName != null ? String(e.stackName) : undefined,
    }));
}

export function safeStringify(v: any): string {
  if (v === null || typeof v === 'undefined') return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

export function parseTimestamp(value?: string): Date | null {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const hasNoTimezone = !/Z|[+-]\d{2}:?\d{2}$/.test(trimmed);
  const withSpace = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d+)?/.test(trimmed);
  const withT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?/.test(trimmed);
  let toParse = trimmed;
  if (hasNoTimezone && (withSpace || withT)) {
    if (withSpace) toParse = trimmed.replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}(?:\.\d+)?)/, '$1T$2');
    toParse = toParse + 'Z';
  }
  const d = new Date(toParse);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

export function formatAbsoluteTimestamp(d: Date): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(d);
  } catch {
    return d.toISOString();
  }
}

export function formatRelativeTimestamp(d: Date): string {
  const diffMs = Date.now() - d.getTime();
  const future = diffMs < 0;
  const absMs = Math.abs(diffMs);

  const sec = Math.floor(absMs / 1000);
  if (sec < 30) return 'just now';

  const min = Math.floor(sec / 60);
  if (min < 60) return future ? `in ${min}m` : `${min}m ago`;

  const hr = Math.floor(min / 60);
  if (hr < 24) return future ? `in ${hr}h` : `${hr}h ago`;

  const day = Math.floor(hr / 24);
  if (day < 7) return future ? `in ${day}d` : `${day}d ago`;

  const week = Math.floor(day / 7);
  if (week < 5) return future ? `in ${week}w` : `${week}w ago`;

  const month = Math.floor(day / 30);
  if (month < 12) return future ? `in ${month}mo` : `${month}mo ago`;

  const year = Math.floor(day / 365);
  return future ? `in ${year}y` : `${year}y ago`;
}

export function getHistoryUserLabel(ev: any): string {
  const u = ev?.user;
  if (u && typeof u === 'object') {
    if (u.displayName) return u.displayName;
    if (u.email) return u.email;
    if (u.id) return u.id;
  }
  return ev?.userId ?? ev?.userName ?? ev?.createdBy ?? ev?.createdByName ?? '-';
}

export function formatHistoryStatusLabel(status: string | undefined): string {
  const s = String(status || '').toLowerCase().replace(/_/g, ' ');
  if (!s) return 'Unknown';
  if (s === 'success') return 'Success';
  if (s === 'partial success') return 'Partial Success';
  if (s === 'failed') return 'Failed';
  if (s === 'processing') return 'Processing';
  if (s === 'queued') return 'Queued';
  if (s === 'pr_created') return 'PR Created';
  return s.charAt(0).toUpperCase() + s.slice(1);
}
